/** Public booking page: /book-a-chat  (?lead=<meta leadgen id> prefills · ?manage=<token> reschedule/cancel) */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Clock, Loader2, PhoneCall, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const TZ = 'Europe/London';
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
const fmtLong = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: TZ });

function SlotPicker({ days, value, onPick }) {
  const [day, setDay] = useState(days[0]?.date || null);
  useEffect(() => { if (!days.find((d) => d.date === day)) setDay(days[0]?.date || null); }, [days, day]);
  const cur = days.find((d) => d.date === day);
  if (!days.length) return <p className="text-sm text-white/70" data-testid="bac-no-slots">No times available at the moment — please check back soon.</p>;
  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="bac-days">
        {days.map((d) => (
          <button key={d.date} type="button" onClick={() => setDay(d.date)} data-testid={`bac-day-${d.date}`}
            className={`shrink-0 rounded-xl px-3 py-2 text-sm font-semibold border transition-colors ${d.date === day ? 'bg-[#D4A24B] text-[#0B0B0E] border-[#D4A24B]' : 'bg-white/5 text-white border-white/15 hover:border-white/40'}`}>
            <div>{d.label.split(' ')[0]}</div><div className="text-xs font-normal opacity-80">{d.label.split(' ').slice(1).join(' ')}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="bac-slots">
        {(cur?.slots || []).map((s) => (
          <button key={s} type="button" onClick={() => onPick(s)} data-testid={`bac-slot-${s}`}
            className={`rounded-lg py-2 text-sm font-semibold border transition-colors ${value === s ? 'bg-[#D4A24B] text-[#0B0B0E] border-[#D4A24B]' : 'bg-white/5 text-white border-white/15 hover:border-[#D4A24B]'}`}>
            {fmtTime(s)}
          </button>
        ))}
      </div>
    </div>
  );
}

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(160deg, #0B0B0E 0%, #14261c 60%, #1f4030 100%)' }}>
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-[11px] tracking-[0.25em] text-[#D4A24B] font-bold">OX RESIN · TRAINING</div>
        <h1 className="text-3xl sm:text-4xl font-bold mt-2 leading-tight" data-testid="bac-title">{title}</h1>
        {sub && <p className="text-white/70 mt-2 text-sm sm:text-base">{sub}</p>}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}

export default function BookAChat() {
  const [params] = useSearchParams();
  const manageToken = params.get('manage');
  const leadId = params.get('lead');
  const [avail, setAvail] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', company_website: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [booking, setBooking] = useState(null);
  const [mode, setMode] = useState('view');

  const loadAvail = useCallback(() => fetch(`${API_URL}/api/public/advisor/availability`).then((r) => r.json()).then(setAvail).catch(() => setAvail({ days: [] })), []);
  useEffect(() => { loadAvail(); }, [loadAvail]);
  useEffect(() => {
    if (leadId) fetch(`${API_URL}/api/public/advisor/lead/${leadId}`).then((r) => r.json()).then((d) => setForm((f) => ({ ...f, name: d.name || '', email: d.email || '', phone: d.phone || '' }))).catch(() => {});
    if (manageToken) fetch(`${API_URL}/api/public/advisor/booking/${manageToken}`).then(async (r) => { if (!r.ok) throw new Error(); setBooking(await r.json()); }).catch(() => setBooking({ missing: true }));
  }, [leadId, manageToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) { toast.error('Pick a day and time first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/advisor/book`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, start: slot, lead: leadId || undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not book');
      setDone(d);
    } catch (err) { toast.error(err.message); loadAvail(); setSlot(null); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm('Cancel this chat?')) return;
    setBusy(true);
    try { const r = await fetch(`${API_URL}/api/public/advisor/booking/${manageToken}/cancel`, { method: 'POST' }); setBooking(await r.json()); toast.success('Cancelled'); } catch { toast.error('Could not cancel'); } finally { setBusy(false); }
  };
  const reschedule = async () => {
    if (!slot) { toast.error('Pick a new time first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/advisor/booking/${manageToken}/reschedule`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ start: slot }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not reschedule');
      setBooking(d); setMode('view'); toast.success('Moved');
    } catch (err) { toast.error(err.message); loadAvail(); } finally { setBusy(false); }
  };

  const advisor = avail?.advisor_name || 'our course advisor';
  const mins = avail?.slot_minutes || 15;
  const field = useMemo(() => (k, label, type = 'text', req = true) => (
    <label className="block text-sm"><span className="text-white/80">{label}</span>
      <Input type={type} required={req} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 bg-white text-gray-900" data-testid={`bac-${k}`} />
    </label>
  ), [form]);

  if (manageToken) {
    if (!booking) return <Shell title="Your booking"><p className="inline-flex items-center gap-2 text-white/70"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p></Shell>;
    if (booking.missing) return <Shell title="Booking not found" sub="That link doesn't match a booking. You can book a new chat below."><a href="/book-a-chat" className="underline text-[#D4A24B]" data-testid="bac-book-new">Book a chat</a></Shell>;
    return (
      <Shell title="Your chat" sub={`with ${booking.advisor_name || advisor}`}>
        <div className="flex items-start gap-3" data-testid="bac-manage">
          {booking.status === 'confirmed' ? <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" /> : <XCircle className="w-6 h-6 text-rose-400 shrink-0" />}
          <div>
            <div className="text-lg font-bold" data-testid="bac-manage-when">{fmtLong(booking.start)}</div>
            <div className="text-sm text-white/70">{booking.status === 'confirmed' ? `We'll call you on ${booking.phone}` : 'This booking has been cancelled.'}</div>
          </div>
        </div>
        {booking.status === 'confirmed' && mode === 'view' && (
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={() => setMode('reschedule')} className="bg-[#D4A24B] text-[#0B0B0E] font-bold hover:bg-[#c3923f]" data-testid="bac-reschedule-btn">Pick a different time</Button>
            <Button variant="outline" onClick={cancel} disabled={busy} className="border-white/30 text-white bg-transparent hover:bg-white/10" data-testid="bac-cancel-btn">Cancel the chat</Button>
          </div>
        )}
        {booking.status === 'confirmed' && mode === 'reschedule' && (
          <div className="mt-5 space-y-4">
            {avail ? <SlotPicker days={avail.days} value={slot} onPick={setSlot} /> : <Loader2 className="w-4 h-4 animate-spin" />}
            <div className="flex gap-2"><Button onClick={reschedule} disabled={busy || !slot} className="bg-[#D4A24B] text-[#0B0B0E] font-bold" data-testid="bac-reschedule-confirm">Move to {slot ? fmtLong(slot) : '…'}</Button><Button variant="ghost" onClick={() => setMode('view')} className="text-white">Back</Button></div>
          </div>
        )}
        {booking.status !== 'confirmed' && <a href="/book-a-chat" className="mt-4 inline-block underline text-[#D4A24B]" data-testid="bac-book-again">Book another time</a>}
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="You're booked in" sub={`${advisor} will call you on ${form.phone}.`}>
        <div className="flex items-start gap-3" data-testid="bac-done">
          <CheckCircle2 className="w-7 h-7 text-emerald-400 shrink-0" />
          <div><div className="text-lg font-bold" data-testid="bac-done-when">{fmtLong(done.start)}</div><div className="text-sm text-white/70 mt-1">A confirmation is on its way to {form.email}{form.phone ? ' and by text' : ''}. We'll send a reminder the day before and an hour before.</div></div>
        </div>
        <a href={`/book-a-chat?manage=${done.manage_token}`} className="mt-5 inline-block text-sm underline text-[#D4A24B]" data-testid="bac-done-manage">Need to change it?</a>
      </Shell>
    );
  }

  return (
    <Shell title={`Book a ${mins}-minute chat`} sub={avail?.intro}>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-[#D4A24B] mb-3"><CalendarDays className="w-4 h-4" /> 1. Pick a day and time <span className="font-normal text-white/50">(UK time)</span></div>
          {avail ? <SlotPicker days={avail.days} value={slot} onPick={setSlot} /> : <p className="inline-flex items-center gap-2 text-white/70 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading times…</p>}
          {slot && <p className="mt-3 text-sm inline-flex items-center gap-1.5 text-emerald-300" data-testid="bac-picked"><Clock className="w-4 h-4" /> {fmtLong(slot)}</p>}
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold text-[#D4A24B]"><PhoneCall className="w-4 h-4" /> 2. Your details</div>
          {field('name', 'Your name')}
          <div className="grid sm:grid-cols-2 gap-3">{field('phone', 'Mobile number (we call you)', 'tel')}{field('email', 'Email', 'email')}</div>
          <label className="block text-sm"><span className="text-white/80">Anything you'd like to cover? <span className="text-white/40">(optional)</span></span>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1 bg-white text-gray-900" data-testid="bac-notes" /></label>
          <input type="text" value={form.company_website} onChange={(e) => setForm((f) => ({ ...f, company_website: e.target.value }))} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        </div>
        <Button type="submit" disabled={busy || !slot} className="w-full h-12 text-base bg-[#D4A24B] text-[#0B0B0E] font-bold hover:bg-[#c3923f] disabled:opacity-50" data-testid="bac-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : slot ? `Book ${fmtLong(slot)}` : 'Pick a time to continue'}
        </Button>
        <p className="text-[11px] text-white/40 text-center">Free, no obligation. We only use your details to arrange this chat.</p>
      </form>
    </Shell>
  );
}
